// @givmo/app-charity — verification-domain card (CPA-WEB6, re-cut by CPA-WEB74).
//
// `organization_domain` is the VERIFICATION TRUST ANCHOR — the eTLD+1 Tier-1 claim auto-verification
// matches a claimant's verified email domain against — and since D-CP-73 it is also the charity's
// WEBSITE, the one the Website input above edits.
//
// This card stays READ-ONLY, and the reason changed. It is no longer "you cannot move this": an
// approved Owner moves it every time they save a website on a new registrable domain. It is that the
// DOMAIN is derived from the website, so editing it here would be a second door onto one value — the
// exact two-field confusion this cut removed. What the card does now is TELL THE TRUTH about the
// coupling: this is what your website resolves to, it is the domain we use to confirm an
// organization, changing one changes the other, and everyone is told.
//
// TWO false sentences have been removed here, and the second is the subtler one.
//   * "different from your public website, which you can edit freely" — false when it shipped,
//     because nothing rendered the display website, and false twice over after D-CP-73.
//   * "It is also how we verified your organization." — false for a Tier-2 reviewer approval, an
//     ownership takeover and a fiscal-sponsor claim (D-CP-7), which are verified by something other
//     than this domain; and the console cannot tell which kind of charity it is rendering, so it
//     cannot branch. The replacement claims only what holds for every charity: this is the domain
//     we USE to confirm an organization.
// Operator-ruled, 2026-09-14 (D-CP-76) — the note below is VERBATIM.

import { Icon, testIdProp } from '@givmo/design-system';
import { Lock, ShieldCheck } from 'lucide-react';

const PREFIX = 'profile-domain';

export interface DomainGuardProps {
  /** The read-only verification domain (eTLD+1). `null`/empty → "not set" copy (unverified / no domain). */
  domain: string | null | undefined;
}

/**
 * Read-only display of the verification `organization_domain` + the honest coupling copy.
 * Rendered for Owners and Members alike (it is never edited HERE by anyone — an Owner moves it by
 * saving a different website above).
 */
export function DomainGuard({ domain }: DomainGuardProps) {
  const value = domain?.trim() ?? '';
  const hasDomain = value.length > 0;

  return (
    <div
      className="flex flex-col gap-8 rounded-lg border border-border-subtle bg-surface-sunken p-14"
      {...testIdProp(`${PREFIX}-guard`)}
    >
      <div className="flex items-center gap-8">
        <Icon icon={ShieldCheck} size={16} className="shrink-0 text-text-tertiary" aria-hidden />
        <span className="font-sans text-s12 font-w600 uppercase tracking-wide text-text-tertiary">
          Verification domain
        </span>
      </div>

      {/* The read-only value. NOT an <input> — a plain, copyable, disabled-looking value block. This is
          the anchor; there is no edit affordance by design. */}
      {hasDomain ? (
        <div
          className="flex items-center gap-8 rounded-md border border-border-subtle bg-neutral-50 px-12 py-10"
          {...testIdProp(`${PREFIX}-value`)}
        >
          <Icon icon={Lock} size={14} className="shrink-0 text-icon-muted" aria-hidden />
          <span className="truncate font-mono text-s14 text-text-strong">{value}</span>
        </div>
      ) : (
        <p className="text-s13 text-text-tertiary" {...testIdProp(`${PREFIX}-empty`)}>
          No verification domain is set for your organization yet.
        </p>
      )}

      {/* The ruled copy (Jonathan, 2026-09-14) — VERBATIM. It names the coupling rather than a
          prohibition: the domain is derived from the website above, so the website is where it
          changes, and the all-admin notice is the control that makes that safe.

          ⚠ The middle sentence was "It is also how we verified your organization." and that was
          FALSE for a whole class of charities — a Tier-2 reviewer approval, an ownership takeover
          and a fiscal-sponsor claim are all verified by something other than this domain (D-CP-7),
          and the console cannot tell which kind it is looking at. The replacement claims only what
          is true for every charity: this is the domain we USE to confirm an organization. */}
      <p className="text-s12 text-text-tertiary" {...testIdProp(`${PREFIX}-note`)}>
        This is the domain your website above points to, and the domain we use to confirm your
        organization. Changing your website changes it, and every admin is notified.
      </p>
    </div>
  );
}
